
import { isSSR } from '../isSSR';
import { debugLog, debugError } from '../../utils/debugLogger';
import { CACHE_TTL } from '../cache/cacheConfig';
import { safeLocalStorage } from '../../utils/safeStorage';

const POSITION_CACHE_KEY = 'gildy_geo_position';
const POSITION_CACHE_EXPIRY = CACHE_TTL.CLIENT_GEOLOCATION; // 15 min - da config centralizzata

// Cache the position in localStorage
export const cachePosition = (position: GeolocationPosition): void => {
  if (isSSR()) return;
  
  try {
    const cachedData = {
      coords: {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        altitude: position.coords.altitude,
        altitudeAccuracy: position.coords.altitudeAccuracy,
        heading: position.coords.heading,
        speed: position.coords.speed
      },
      timestamp: position.timestamp || Date.now()
    };
    
    safeLocalStorage.setItem(POSITION_CACHE_KEY, JSON.stringify(cachedData));
    debugLog('general', `Position cached: ${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`, undefined, 'cachePosition');
  } catch (error) {
    debugError('general', "Error caching position:", error, 'cachePosition');
  }
};

// Get the cached position from localStorage
export const getCachedPosition = (): GeolocationPosition | null => {
  if (isSSR()) return null;
  
  try {
    const cachedDataString = safeLocalStorage.getItem(POSITION_CACHE_KEY);
    if (!cachedDataString) return null;
    
    const cachedData = JSON.parse(cachedDataString);
    
    // Validate cached data has required properties
    if (!cachedData || !cachedData.coords || 
        typeof cachedData.coords.latitude !== 'number' || 
        typeof cachedData.coords.longitude !== 'number') {
      debugLog('general', "Invalid cached position data, clearing cache", undefined, 'getCachedPosition');
      safeLocalStorage.removeItem(POSITION_CACHE_KEY);
      return null;
    }
    
    // Check if cache is expired
    const now = Date.now();
    const cacheAge = now - (cachedData.timestamp || 0);
    
    if (cacheAge > POSITION_CACHE_EXPIRY) {
      debugLog('general', "Cached position expired, clearing cache", undefined, 'getCachedPosition');
      safeLocalStorage.removeItem(POSITION_CACHE_KEY);
      return null;
    }
    
    debugLog('general', `Using cached position from: ${new Date(cachedData.timestamp).toLocaleTimeString()}`, undefined, 'getCachedPosition');
    return cachedData as GeolocationPosition;
  } catch (error) {
    debugError('general', "Error retrieving cached position:", error, 'getCachedPosition');
    // Clear corrupted cache
    try {
      safeLocalStorage.removeItem(POSITION_CACHE_KEY);
    } catch (e) {
      // Ignore errors during cleanup
    }
    return null;
  }
};

// Get age of cached position in minutes
export const getCachedPositionAge = (): number | null => {
  if (isSSR()) return null;
  
  try {
    const cachedDataString = safeLocalStorage.getItem(POSITION_CACHE_KEY);
    if (!cachedDataString) return null;
    
    const cachedData = JSON.parse(cachedDataString);
    
    if (!cachedData || !cachedData.timestamp) return null;
    
    const now = Date.now();
    const cacheAgeInMs = now - cachedData.timestamp;
    return Math.floor(cacheAgeInMs / (1000 * 60)); // Convert to minutes
  } catch (error) {
    debugError('general', "Error calculating cached position age:", error, 'getCachedPositionAge');
    return null;
  }
};

// Clear the position cache
export const clearPositionCache = (): void => {
  if (isSSR()) return;
  
  try {
    safeLocalStorage.removeItem(POSITION_CACHE_KEY);
    debugLog('general', "Position cache cleared", undefined, 'clearPositionCache');
  } catch (error) {
    debugError('general', "Error clearing position cache:", error, 'clearPositionCache');
  }
};

// Check if cached position is older than specified age in minutes
export const isCacheExpired = (maxAgeInMinutes: number = 30): boolean => {
  if (isSSR()) return true;
  
  try {
    const cachedDataString = safeLocalStorage.getItem(POSITION_CACHE_KEY);
    if (!cachedDataString) return true;
    
    const cachedData = JSON.parse(cachedDataString);
    if (!cachedData || !cachedData.timestamp) return true;
    
    const now = Date.now();
    const cacheAgeInMs = now - cachedData.timestamp;
    const cacheAgeInMinutes = cacheAgeInMs / (1000 * 60);
    
    return cacheAgeInMinutes > maxAgeInMinutes;
  } catch (error) {
    debugError('general', "Error checking cache expiry:", error, 'isCacheExpired');
    return true;
  }
};
